/**
 * Founder investor CRM ("Investor Hygiene"). A searchable/filterable contact
 * table with inline editing of stage and dates, a slide-panel detail (full
 * fields + per-contact notes), CSV import/export, and copyable email templates.
 *
 * Stage and date edits persist immediately and update local state optimistically.
 * Cells that contain their own controls stopPropagation so clicking them does
 * not also open the detail panel.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { getCRM, createContact, updateContact, deleteContact, getContactNotes, addContactNote, exportContacts, importContacts } from '../services/apiService';
import FounderNotesWidget from '../components/FounderNotesWidget';
import SlidePanel from '../components/SlidePanel';

const STAGES = [
  { value: 'prospecting',  label: 'Prospecting',  color: '#6b7280', bg: '#f3f4f6' },
  { value: 'contacted',    label: 'Contacted',     color: '#2563eb', bg: '#eff6ff' },
  { value: 'interested',   label: 'Interested',    color: '#d97706', bg: '#fef3c7' },
  { value: 'term_sheet',   label: 'Term Sheet',    color: '#7c3aed', bg: '#f5f3ff' },
  { value: 'committed',    label: 'Committed',     color: '#059669', bg: '#ecfdf5' },
  { value: 'closed_won',   label: 'Closed Won',    color: '#15803d', bg: '#dcfce7' },
  { value: 'closed_lost',  label: 'Closed Lost',   color: '#dc2626', bg: '#fee2e2' },
];
const STAGE_MAP = Object.fromEntries(STAGES.map((s) => [s.value, s]));

const EMAIL_TEMPLATES = [
  {
    label: 'Initial Outreach',
    body: `Subject: Introduction — [Your Company]
BCC: ceg@venturereadiness.org

Hi [Name],

I came across [Organization] and was impressed by [specific detail]. I'm building [Company], which [one-sentence pitch].

We're currently raising a [raise amount] [round type]. I'd love to share more and hear your thoughts.

Would you be open to a 20-minute call this week or next?

Best,
[Your Name]
[Company] | [Website]`,
  },
  {
    label: 'Follow-Up',
    body: `Subject: Following up — [Your Company]
BCC: ceg@venturereadiness.org

Hi [Name],

I wanted to follow up on my note from [X days/weeks] ago about [Company].

We've made some progress since then — [brief update, e.g. closed X customers, hit Y milestone]. I think the timing could be interesting for [Organization].

Would you have 20 minutes this week to connect?

Best,
[Your Name]
[Company] | [Website]`,
  },
  {
    label: 'Post-Meeting',
    body: `Subject: Great connecting — [Your Company]
BCC: ceg@venturereadiness.org

Hi [Name],

Thank you for taking the time to meet today. I really appreciated your perspective on [topic discussed].

As a follow-up, I'm attaching [deck / one-pager / data room link]. The key points we covered:
  • [Point 1]
  • [Point 2]
  • [Point 3]

Happy to answer any questions or get on another call at your convenience.

Best,
[Your Name]
[Company] | [Website]`,
  },
  {
    label: 'Investor Update',
    body: `Subject: [Company] — [Month] Update
BCC: ceg@venturereadiness.org

Hi [Name],

Quick update on [Company]:

Highlights
  • [Milestone or metric, e.g. Revenue: $X MRR (+Y% MoM)]
  • [Customer win or partnership]
  • [Product launch or feature shipped]

Challenges
  • [Honest challenge you're working through]

Ask
  • [Specific ask, e.g. intro to X, feedback on Y]

Thanks for your continued support.

[Your Name]
[Company] | [Website]`,
  },
];

// Parse a YYYY-MM-DD string as a local date (avoids the UTC shift that
// new Date('YYYY-MM-DD') would introduce).
function parseLocalDate(str) {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

// Red if the follow-up is overdue, amber if within 3 days, null otherwise.
function followUpColor(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((parseLocalDate(dateStr) - today) / 86400000);
  if (diff < 0)   return '#dc2626';
  if (diff <= 3)  return '#d97706';
  return null;
}

/** Founder-facing investor pipeline table with detail panel and CSV/templates. */
export default function InvestorCRM() {
  const [contacts, setContacts] = useState([]);
  const [assignedEir, setAssignedEir] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [showAddRow, setShowAddRow] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    getCRM()
      .then(({ contacts: c, assigned_eir }) => { setContacts(c); setAssignedEir(assigned_eir); })
      .finally(() => setLoading(false));
  }, []);

  const handleStageChange = useCallback(async (id, stage) => {
    setContacts((prev) => prev.map((c) => c.id === id ? { ...c, stage } : c));
    await updateContact(id, { stage });
  }, []);

  const handleDateChange = useCallback(async (id, field, value) => {
    setContacts((prev) => prev.map((c) => c.id === id ? { ...c, [field]: value || null } : c));
    await updateContact(id, { [field]: value || null });
  }, []);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    await deleteContact(id);
    setContacts((prev) => prev.filter((c) => c.id !== id));
    if (selectedContact?.id === id) setPanelOpen(false);
  }, [selectedContact]);

  const handleCreate = useCallback(async (data) => {
    const created = await createContact(data);
    setContacts((prev) => [...prev, created]);
    setShowAddRow(false);
  }, []);

  const handleUpdate = useCallback(async (id, data) => {
    const updated = await updateContact(id, data);
    setContacts((prev) => prev.map((c) => c.id === id ? { ...c, ...updated } : c));
    setSelectedContact((prev) => prev?.id === id ? { ...prev, ...updated } : prev);
  }, []);

  const openPanel = useCallback((contact) => {
    setSelectedContact(contact);
    setPanelOpen(true);
  }, []);

  const filtered = contacts.filter((c) => {
    const q = search.toLowerCase();
    const matchSearch = !q || c.name.toLowerCase().includes(q) || (c.organization || '').toLowerCase().includes(q) || (c.industry || '').toLowerCase().includes(q);
    return matchSearch && (!stageFilter || c.stage === stageFilter);
  });

  return (
    <div style={styles.page}>
      <div style={styles.inner}>

        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Investor Hygiene</h1>
            {assignedEir && <p style={styles.eirLine}>Your EIR: <strong>{assignedEir.name}</strong></p>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={styles.csvBtn} onClick={() => setShowImport(true)}>Import CSV</button>
            <button style={styles.csvBtn} disabled={exporting} onClick={async () => {
              setExporting(true);
              try { await exportContacts(); } finally { setExporting(false); }
            }}>
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
            <button style={styles.templateBtn} onClick={() => setShowTemplate(true)}>
              Email Template
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ marginLeft: 5 }}>
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>

        <div style={styles.controls}>
          <div style={styles.searchWrap}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={styles.searchIcon}>
              <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
              <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input style={styles.searchInput} placeholder="Search contacts…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <select style={styles.filterSelect} value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}>
            <option value="">All stages</option>
            {STAGES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.thead}>
                <th style={{ ...styles.th, width: 36 }}>#</th>
                <th style={{ ...styles.th, minWidth: 140 }}>Name</th>
                <th style={{ ...styles.th, minWidth: 140 }}>Organization</th>
                <th style={{ ...styles.th, width: 44 }}>Email</th>
                <th style={{ ...styles.th, minWidth: 110 }}>Industry</th>
                <th style={{ ...styles.th, minWidth: 140 }}>Stage</th>
                <th style={{ ...styles.th, minWidth: 130 }}>Last contacted</th>
                <th style={{ ...styles.th, minWidth: 130 }}>Next follow-up</th>
                <th style={{ ...styles.th, width: 36 }} />
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} style={styles.emptyCell}>Loading…</td></tr>}
              {!loading && filtered.length === 0 && !showAddRow && (
                <tr><td colSpan={9} style={styles.emptyCell}>No contacts yet. Click "+ Add Contact" below.</td></tr>
              )}
              {filtered.map((c, i) => (
                <ContactRow key={c.id} contact={c} rowNum={i + 1}
                  onStageChange={handleStageChange}
                  onDateChange={handleDateChange}
                  onDelete={handleDelete}
                  onClick={() => openPanel(c)}
                />
              ))}
              {showAddRow && <AddContactRow onSave={handleCreate} onCancel={() => setShowAddRow(false)} />}
            </tbody>
          </table>
        </div>

        <button style={styles.addBtn} onClick={() => setShowAddRow(true)}>+ Add Contact</button>

      </div>

      <SlidePanel open={panelOpen} onClose={() => setPanelOpen(false)} title={selectedContact?.name || ''}>
        {selectedContact && (
          <ContactDetail
            contact={selectedContact}
            onUpdate={handleUpdate}
            onDelete={(id) => { handleDelete(id); setPanelOpen(false); }}
            onClose={() => setPanelOpen(false)}
          />
        )}
      </SlidePanel>

      {showTemplate && <TemplateModal onClose={() => setShowTemplate(false)} />}
      {showImport && (
        <ImportCSVModal
          onClose={() => setShowImport(false)}
          onImported={(result) => {
            setShowImport(false);
            getCRM().then(({ contacts: c }) => setContacts(c));
            if (result.imported > 0) alert(`Imported ${result.imported} contact${result.imported !== 1 ? 's' : ''}${result.skipped > 0 ? `, skipped ${result.skipped}` : ''}.`);
          }}
        />
      )}
      <FounderNotesWidget />
    </div>
  );
}

// One contact row. Stage select, date inputs, email link, and delete each
// stopPropagation so they don't trigger the row's open-detail onClick.
function ContactRow({ contact, rowNum, onStageChange, onDateChange, onDelete, onClick }) {
  const stage = STAGE_MAP[contact.stage] || STAGE_MAP.prospecting;
  const followUp = followUpColor(contact.next_follow_up_date);
  return (
    <tr style={styles.tr} onClick={onClick}>
      <td style={styles.numCell}>{rowNum}</td>
      <td style={{ ...styles.td, fontWeight: 700 }}>{contact.name}</td>
      <td style={styles.td}>{contact.organization || <span style={styles.dim}>—</span>}</td>
      <td style={styles.td} onClick={(e) => e.stopPropagation()}>
        {contact.email
          ? <a href={`mailto:${contact.email}`} style={styles.emailBtn} title={contact.email}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/></svg>
            </a>
          : <span style={styles.dim}>—</span>}
      </td>
      <td style={styles.td}>{contact.industry || <span style={styles.dim}>—</span>}</td>
      <td style={styles.td} onClick={(e) => e.stopPropagation()}>
        <select value={contact.stage} onChange={(e) => onStageChange(contact.id, e.target.value)}
          style={{ ...styles.stageSelect, background: stage.bg, color: stage.color }}>
          {STAGES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </td>
      <td style={styles.td} onClick={(e) => e.stopPropagation()}>
        <input type="date" value={contact.last_contact_date || ''} style={styles.dateInput}
          onChange={(e) => onDateChange(contact.id, 'last_contact_date', e.target.value)} />
      </td>
      <td style={styles.td} onClick={(e) => e.stopPropagation()}>
        <input type="date" value={contact.next_follow_up_date || ''}
          style={{ ...styles.dateInput, ...(followUp ? { color: followUp, fontWeight: 800 } : {}) }}
          onChange={(e) => onDateChange(contact.id, 'next_follow_up_date', e.target.value)} />
      </td>
      <td style={styles.td} onClick={(e) => e.stopPropagation()}>
        <button style={styles.deleteRowBtn} onClick={() => onDelete(contact.id)} title="Delete">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
            <path d="M10 11v6M14 11v6M9 6V4h6v2" />
          </svg>
        </button>
      </td>
    </tr>
  );
}

function AddContactRow({ onSave, onCancel }) {
  const [name, setName] = useState('');
  const [org, setOrg] = useState('');
  const [email, setEmail] = useState('');
  const [industry, setIndustry] = useState('');
  const [stage, setStage] = useState('prospecting');
  const [saving, setSaving] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);

  async function save() {
    if (!name.trim()) return;
    setSaving(true);
    try { await onSave({ name: name.trim(), organization: org || null, email: email || null, industry: industry || null, stage }); }
    finally { setSaving(false); }
  }

  return (
    <tr style={{ ...styles.tr, background: '#f9fdfb' }}>
      <td style={styles.numCell}>+</td>
      <td style={styles.td}><input autoFocus style={styles.inlineInput} placeholder="Name *" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && save()} /></td>
      <td style={styles.td}><input style={styles.inlineInput} placeholder="Organization" value={org} onChange={(e) => setOrg(e.target.value)} /></td>
      <td style={{ ...styles.td, position: 'relative', overflow: 'visible' }}>
        <input
          style={emailFocused ? styles.inlineInputEmailExpanded : styles.inlineInput}
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onFocus={() => setEmailFocused(true)}
          onBlur={() => setEmailFocused(false)}
        />
      </td>
      <td style={styles.td}><input style={styles.inlineInput} placeholder="Industry" value={industry} onChange={(e) => setIndustry(e.target.value)} /></td>
      <td style={styles.td}>
        <select value={stage} onChange={(e) => setStage(e.target.value)}
          style={{ ...styles.stageSelect, background: STAGE_MAP[stage]?.bg, color: STAGE_MAP[stage]?.color }}>
          {STAGES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </td>
      <td colSpan={2} style={styles.td}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={styles.saveRowBtn} onClick={save} disabled={saving || !name.trim()}>{saving ? '…' : 'Save'}</button>
          <button style={styles.cancelRowBtn} onClick={onCancel}>Cancel</button>
        </div>
      </td>
      <td />
    </tr>
  );
}

// Slide-panel editor for a single contact: a local form draft saved via
// onUpdate, plus a notes thread loaded and appended for this contact.
function ContactDetail({ contact, onUpdate, onDelete }) {
  const [form, setForm] = useState({ ...contact });
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [saving, setSaving] = useState(false);
  const stage = STAGE_MAP[form.stage] || STAGE_MAP.prospecting;

  useEffect(() => { getContactNotes(contact.id).then(setNotes); }, [contact.id]);

  async function save() {
    setSaving(true);
    try { await onUpdate(contact.id, form); } finally { setSaving(false); }
  }

  async function submitNote() {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      const note = await addContactNote(contact.id, noteText.trim());
      setNotes((prev) => [...prev, note]);
      setNoteText('');
    } finally { setSavingNote(false); }
  }

  function Field({ label, k, type = 'text' }) {
    return (
      <div style={panelStyles.field}>
        <label style={panelStyles.label}>{label}</label>
        <input style={panelStyles.input} type={type} value={form[k] || ''}
          onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} />
      </div>
    );
  }

  return (
    <div style={panelStyles.wrap}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ ...panelStyles.stageBadge, background: stage.bg, color: stage.color }}>{stage.label}</span>
        <button style={panelStyles.deleteBtn} onClick={() => onDelete(contact.id)}>Delete contact</button>
      </div>

      <div style={panelStyles.grid}>
        <Field label="Name" k="name" />
        <Field label="Organization" k="organization" />
        <Field label="Email" k="email" type="email" />
        <Field label="Phone" k="phone" type="tel" />
        <Field label="Industry" k="industry" />
        <div style={panelStyles.field}>
          <label style={panelStyles.label}>Stage</label>
          <select style={{ ...panelStyles.input, background: stage.bg, color: stage.color, fontWeight: 800 }}
            value={form.stage} onChange={(e) => setForm((f) => ({ ...f, stage: e.target.value }))}>
            {STAGES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <Field label="Last Contacted" k="last_contact_date" type="date" />
        <Field label="Next Follow-up" k="next_follow_up_date" type="date" />
      </div>

      <button style={panelStyles.saveBtn} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
      <div style={panelStyles.divider} />

      <p style={panelStyles.notesTitle}>
        Notes {notes.length > 0 && <span style={panelStyles.noteCount}>{notes.length}</span>}
      </p>
      <div style={panelStyles.noteInput}>
        <textarea style={panelStyles.textarea} rows={3} placeholder="Add a note…"
          value={noteText} onChange={(e) => setNoteText(e.target.value)} />
        <button style={panelStyles.addNoteBtn} onClick={submitNote} disabled={savingNote || !noteText.trim()}>
          {savingNote ? '…' : 'Add'}
        </button>
      </div>
      <div style={panelStyles.notesList}>
        {notes.length === 0 && <p style={panelStyles.dimText}>No notes yet.</p>}
        {[...notes].reverse().map((n) => (
          <div key={n.id} style={panelStyles.noteItem}>
            <p style={panelStyles.noteBody}>{n.body}</p>
            <p style={panelStyles.noteDate}>{n.created_at ? new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// Tabbed library of copyable outreach email templates (EMAIL_TEMPLATES); each
// pre-fills a BCC to the program so staff can track founder outreach.
function TemplateModal({ onClose }) {
  const [activeTab, setActiveTab] = useState(0);
  const [copied, setCopied] = useState(false);

  function copy() {
    navigator.clipboard.writeText(EMAIL_TEMPLATES[activeTab].body);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div style={modalStyles.backdrop} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyles.modal}>
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>Email Templates</h2>
          <button style={modalStyles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={modalStyles.tabs}>
          {EMAIL_TEMPLATES.map((t, i) => (
            <button
              key={t.label}
              style={{ ...modalStyles.tab, ...(activeTab === i ? modalStyles.tabActive : {}) }}
              onClick={() => { setActiveTab(i); setCopied(false); }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <p style={modalStyles.hint}>Customize the bracketed fields before sending.</p>
        <pre style={modalStyles.template}>{EMAIL_TEMPLATES[activeTab].body}</pre>
        <div style={modalStyles.actions}>
          <button style={modalStyles.copyBtn} onClick={copy}>{copied ? 'Copied!' : 'Copy to Clipboard'}</button>
          <button style={modalStyles.cancelBtn} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { flex: 1, overflowY: 'auto', background: 'linear-gradient(to bottom, var(--color-frost) 0%, var(--color-mint-dark) 100%) fixed', padding: 'var(--space-8)' },
  inner: { maxWidth: "98%", margin: "0 auto" },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 'var(--space-5)', paddingTop: 'var(--space-2)' },
  title: { fontFamily: 'var(--font-accent)', fontSize: 'var(--font-size-page-title)', fontWeight: 'var(--font-weight-page-title)', color: 'var(--color-rust)', lineHeight: 1.1, marginBottom: 4 },
  eirLine: { fontFamily: 'var(--font-body)', fontSize: 'var(--font-size-page-subtitle)', color: 'var(--color-forest)', opacity: 0.7 },
  templateBtn: { display: 'flex', alignItems: 'center', padding: '8px 16px', borderRadius: 'var(--radius-sm)', fontSize: 15, fontWeight: 700, fontFamily: 'var(--font-body)', color: 'var(--color-forest)', background: '#fff', border: '1.5px solid var(--color-mint-dark)', cursor: 'pointer', flexShrink: 0, marginTop: 6 },
  csvBtn: { display: 'flex', alignItems: 'center', padding: '8px 14px', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-body)', color: 'var(--color-forest)', background: 'var(--color-mint)', border: '1.5px solid var(--color-mint-dark)', cursor: 'pointer', flexShrink: 0, marginTop: 6 },
  controls: { display: 'flex', gap: 12, marginBottom: 'var(--space-3)', flexWrap: 'wrap' },
  searchWrap: { flex: 1, minWidth: 200, position: 'relative' },
  searchIcon: { position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-forest)', opacity: 0.4 },
  searchInput: { width: '100%', padding: '8px 12px 8px 34px', borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--color-mint-dark)', background: '#fff', fontFamily: 'var(--font-body)', fontSize: 20, color: 'var(--color-forest)', boxSizing: 'border-box', outline: 'none' },
  filterSelect: { padding: '8px 12px', borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--color-mint-dark)', background: '#fff', fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', outline: 'none' },
  tableWrap: { background: '#fff', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-card)', overflow: 'auto', marginBottom: 8 },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 700 },
  thead: { background: '#f8fafb' },
  th: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 800, color: 'var(--color-forest)', opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f5f5f5', cursor: 'pointer' },
  td: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', padding: '10px 12px', verticalAlign: 'middle' },
  numCell: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.35, padding: '10px 12px', textAlign: 'center', verticalAlign: 'middle' },
  emptyCell: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.45, padding: '28px 16px', textAlign: 'center', fontStyle: 'italic' },
  dim: { opacity: 0.35 },
  stageSelect: { padding: '3px 8px', borderRadius: 'var(--radius-full)', border: 'none', fontSize: 15, fontWeight: 800, fontFamily: 'var(--font-body)', cursor: 'pointer', outline: 'none' },
  dateInput: { border: 'none', background: 'transparent', fontFamily: 'var(--font-body)', fontSize: 16, color: 'var(--color-forest)', outline: 'none', cursor: 'pointer', width: 130 },
  emailBtn: { color: 'var(--color-forest)', opacity: 0.6, display: 'flex', alignItems: 'center' },
  deleteRowBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', opacity: 0.6, padding: 2, display: 'flex', alignItems: 'center' },
  inlineInput: { border: 'none', borderBottom: '1px solid var(--color-mint-dark)', background: 'transparent', fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', outline: 'none', width: '100%', padding: '2px 0' },
  inlineInputEmailExpanded: { position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 10, width: 240, border: 'none', borderBottom: '1.5px solid var(--color-forest)', background: '#fff', fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', outline: 'none', padding: '2px 4px', boxShadow: '0 4px 16px rgba(33,81,45,0.12)', borderRadius: 2 },
  saveRowBtn: { padding: '4px 14px', borderRadius: 'var(--radius-sm)', fontSize: 16, fontWeight: 800, fontFamily: 'var(--font-body)', color: '#fff', background: 'var(--color-forest)', border: 'none', cursor: 'pointer' },
  cancelRowBtn: { padding: '4px 10px', borderRadius: 'var(--radius-sm)', fontSize: 16, fontWeight: 700, fontFamily: 'var(--font-body)', color: 'var(--color-forest)', background: 'none', border: '1px solid var(--color-mint-dark)', cursor: 'pointer' },
  addBtn: { background: 'none', border: 'none', fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.6, cursor: 'pointer', padding: '6px 0' },
};

const panelStyles = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' },
  stageBadge: { padding: '3px 12px', borderRadius: 'var(--radius-full)', fontSize: 15, fontWeight: 800 },
  deleteBtn: { fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 700, color: '#dc2626', background: 'none', border: '1px solid #fca5a5', borderRadius: 'var(--radius-sm)', padding: '4px 12px', cursor: 'pointer' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 800, color: 'var(--color-forest)', opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' },
  input: { padding: '7px 10px', borderRadius: 'var(--radius-sm)', border: '1.5px solid #e0e0e0', background: '#fff', fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', outline: 'none', width: '100%', boxSizing: 'border-box' },
  saveBtn: { padding: '9px', borderRadius: 'var(--radius-sm)', fontSize: 15, fontWeight: 800, fontFamily: 'var(--font-body)', color: '#fff', background: 'var(--color-forest)', border: 'none', cursor: 'pointer', width: '100%' },
  divider: { height: 1, background: '#f0f0f0', margin: '4px 0' },
  notesTitle: { fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 800, color: 'var(--color-forest)', display: 'flex', alignItems: 'center', gap: 8 },
  noteCount: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 800, background: 'var(--color-mint-dark)', color: 'var(--color-forest)', padding: '1px 7px', borderRadius: 'var(--radius-full)' },
  noteInput: { display: 'flex', gap: 8, alignItems: 'flex-start' },
  textarea: { flex: 1, padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '1.5px solid #e0e0e0', fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', outline: 'none', resize: 'none', lineHeight: 1.5 },
  addNoteBtn: { padding: '8px 14px', borderRadius: 'var(--radius-sm)', fontSize: 16, fontWeight: 800, fontFamily: 'var(--font-body)', color: '#fff', background: 'var(--color-forest)', border: 'none', cursor: 'pointer', flexShrink: 0 },
  notesList: { display: 'flex', flexDirection: 'column', gap: 8 },
  noteItem: { background: '#f8fafb', borderRadius: 'var(--radius-sm)', padding: '10px 12px', borderLeft: '3px solid var(--color-mint-dark)' },
  noteBody: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', lineHeight: 1.5, whiteSpace: 'pre-wrap' },
  noteDate: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.45, marginTop: 4 },
  dimText: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.4, fontStyle: 'italic' },
};

const modalStyles = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 },
  modal: { background: '#fff', borderRadius: 12, padding: 28, width: '90%', maxWidth: 580, boxShadow: '0 20px 48px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', gap: 14 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 800, color: 'var(--color-forest)' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--color-forest)', opacity: 0.5 },
  tabs: { display: 'flex', gap: 6, flexWrap: 'wrap', borderBottom: '1.5px solid var(--color-mint-dark)', paddingBottom: 10 },
  tab: { padding: '5px 14px', borderRadius: 'var(--radius-sm)', fontSize: 14, fontWeight: 700, fontFamily: 'var(--font-body)', color: 'var(--color-forest)', background: 'none', border: '1.5px solid var(--color-mint-dark)', cursor: 'pointer', opacity: 0.6 },
  tabActive: { background: 'var(--color-forest)', color: '#fff', border: '1.5px solid var(--color-forest)', opacity: 1 },
  hint: { fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--color-forest)', opacity: 0.55, margin: 0 },
  template: { background: '#f8fafb', borderRadius: 8, padding: 16, fontFamily: 'monospace', fontSize: 14, color: 'var(--color-forest)', lineHeight: 1.7, whiteSpace: 'pre-wrap', overflowY: 'auto', maxHeight: 260, border: '1px solid #e8e8e8' },
  actions: { display: 'flex', gap: 10 },
  copyBtn: { flex: 1, padding: 10, borderRadius: 8, fontSize: 15, fontWeight: 800, fontFamily: 'var(--font-body)', color: '#fff', background: 'var(--color-forest)', border: 'none', cursor: 'pointer' },
  cancelBtn: { padding: '10px 18px', borderRadius: 8, fontSize: 15, fontWeight: 700, fontFamily: 'var(--font-body)', color: 'var(--color-forest)', background: 'none', border: '1.5px solid var(--color-mint-dark)', cursor: 'pointer' },
};

// CSV import dialog. Only the `name` column is required; offers a downloadable
// template and reports imported/skipped counts via onImported.
function ImportCSVModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);

  async function handleImport() {
    if (!file) return;
    setImporting(true);
    setError(null);
    try {
      const result = await importContacts(file);
      onImported(result);
    } catch (err) {
      setError(err.response?.data?.detail || 'Import failed. Check the file format and try again.');
    } finally {
      setImporting(false);
    }
  }

  const CSV_TEMPLATE = 'name,organization,email,phone,industry,stage,last_contact_date,next_follow_up_date\nJane Smith,Acme Capital,jane@acme.com,,Fintech,prospecting,2026-01-15,2026-02-01';

  function downloadTemplate() {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'investor-contacts-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div style={modalStyles.backdrop} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyles.modal}>
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>Import Contacts from CSV</h2>
          <button style={modalStyles.closeBtn} onClick={onClose}>✕</button>
        </div>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--color-forest)', opacity: 0.7, marginBottom: 16, lineHeight: 1.5 }}>
          Upload a CSV file with your investor contacts. Required column: <strong>name</strong>. All other columns are optional.
        </p>
        <button
          onClick={downloadTemplate}
          style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: '#1a5276', background: '#e8f4fd', border: '1.5px solid #aed6f1', borderRadius: 'var(--radius-sm)', padding: '6px 14px', cursor: 'pointer', marginBottom: 20 }}
        >
          Download template CSV
        </button>
        <input
          type="file"
          accept=".csv"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          style={{ display: 'block', marginBottom: 16, fontFamily: 'var(--font-body)', fontSize: 14 }}
        />
        {error && <p style={{ color: '#b71c1c', fontFamily: 'var(--font-body)', fontSize: 13, marginBottom: 12 }}>{error}</p>}
        <div style={modalStyles.actions}>
          <button
            style={{ ...modalStyles.copyBtn, opacity: !file || importing ? 0.6 : 1 }}
            onClick={handleImport}
            disabled={!file || importing}
          >
            {importing ? 'Importing…' : 'Import'}
          </button>
          <button style={modalStyles.cancelBtn} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
